import { publicRegimeLabel, hasRegimeLabel } from "@shared/publicRegime";
/**
 * Public terminology boundary.
 *
 * Backend identifiers are data, never customer copy. A value that is not in a
 * reviewed dictionary fails closed to a useful generic phrase; it is never
 * title-cased or underscore-stripped into something that merely looks human.
 */
const PUBLIC_MARKET_LABELS: Record<string, string> = {
  multi_indicator_confluence: "Several market checks agree",
  momentum_shift: "Market direction changed",
  market_alert: "Broad market warning",
  flash_crash_precursor: "Sudden-fall warning",
  flash_crash_detected: "Sudden-fall warning",
  forecast_72h: "Three-day outlook",
  risk_intelligence_72h: "Three-day risk check",
  risk_intelligence_systemic: "Broad-market risk check",
  risk_intelligence_measured: "Measured risk check",
  risk_intelligence_news: "News risk check",
  regime_classifier: "Market direction check",
  regime_change: "Market conditions changed",
  early_warning_6h: "Six-hour early warning",
  early_warning_synthesis: "Combined early warning",
  committee_verdict: "Fahali's combined reading",
  canonical_verdict: "Fahali's final reading",
  crash_predictor: "Sudden-fall check",
  cascade_engine: "Forced-selling chain check",
  leverage_proxy: "Borrowing stress check",
  funding_proxy: "Borrowing-cost check",
  volatility_iv_rv: "Expected move versus recent moves",
  temporal_arbitrage: "Signals disagree across time",
  sentiment_news: "News mood",
  order_flow_imbalance: "Buying versus selling pressure",
  market_depth_deterioration: "Available liquidity is thinning",
  liquidation_risk: "Forced-selling risk",
  funding_stress: "Borrowing stress",
  stablecoin: "Stablecoin stability check",
  tail_dependence: "Hidden correlation chain",
  correlation: "Shared-movement check",
  leverage: "Borrowing stress",
  risk_intelligence: "Risk check",
  momentum: "Price direction",
  volatility: "Size of market moves",
  anomaly: "Unusual market activity",
  pattern: "Price pattern check",
  microstructure: "Trading-behaviour check",
  ofi: "Buying versus selling pressure",
  ml_ensemble: "Combined market checks",
  market_regime: "Current market conditions",
};

const PUBLIC_CATEGORY_LABELS: Record<string, string> = {
  other: "Broad market",
  market: "Broad market",
  momentum: "Price direction",
  anomaly: "Unusual activity",
  risk: "Risk",
  flow: "Buying and selling",
  sentiment: "Market mood",
  forecast: "What may happen next",
  regime: "Market conditions",
  technical: "Price behaviour",
  ml: "Combined checks",
  crypto: "Crypto",
  equity: "Stock",
  etf: "Fund",
  forex: "Currency",
  commodity: "Commodity",
  investment: "Investment",
};

const PUBLIC_DETAIL_LABELS: Record<string, string> = {
  critical: "Needs immediate attention",
  high: "High priority",
  moderate: "Worth watching",
  medium: "Worth watching",
  low: "Low priority",
  active: "Active",
  inactive: "Inactive",
  paused: "Paused",
  high_conviction: "Several checks agree",
  developing: "Still developing",
  noise: "May be ordinary market noise",
  hedge: "Protect",
  reduce: "Reduce exposure",
  monitor: "Keep watching",
  seize_opportunity: "Review the opportunity",
  iceberg: "A large order is being split into smaller trades",
  order_splitting: "A large order is being split into smaller trades",
  stealth_execution: "Trading designed to limit visible market impact",
  unknown_institutional: "Unidentified market activity",
  unknown: "Not identified",
  exchange_dark_pool: "Trading venue attribution unavailable",
  broker_dark_pool: "Trading venue attribution unavailable",
  crossing_network: "Trading venue attribution unavailable",
  up: "Moving up",
  down: "Moving down",
  buying: "Buyer-initiated pressure",
  selling: "Seller-initiated pressure",
  accumulating: "Buying pressure is building",
  distributing: "Selling pressure is building",
  hedging: "Defensive risk signals",
};

function keyOf(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\w+Type\./, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

export function publicMarketLabel(value: unknown, fallback = "Market check"): string {
  const key = keyOf(value);
  if (!key) return fallback;
  // Regimes are NOT restated here. This map used to carry its own copies of
  // nine regime keys; they agreed in wording, which is exactly why nobody
  // noticed there were two dictionaries. One author, consulted first.
  if (hasRegimeLabel(key)) return publicRegimeLabel(key);
  return PUBLIC_MARKET_LABELS[key] ?? fallback;
}

export function publicCategoryLabel(value: unknown, fallback = "Market information"): string {
  const key = keyOf(value);
  if (!key) return fallback;
  return PUBLIC_CATEGORY_LABELS[key] ?? fallback;
}

export function publicDetailLabel(value: unknown, fallback = "More detail is not available"): string {
  const key = keyOf(value);
  if (!key) return fallback;
  return PUBLIC_DETAIL_LABELS[key] ?? fallback;
}

export function publicScenarioLabel(value: unknown): string {
  const key = keyOf(value);
  const labels: Record<string, string> = {
    market_crash: "Broad market fall",
    flash_crash: "Sudden market fall",
    crypto_crash: "Sudden crypto fall",
    equity_crash: "Sudden stock-market fall",
    liquidity_crisis: "Markets become difficult to trade",
    correlation_spike: "Holdings start falling together",
    severe_drawdown: "Severe portfolio loss",
    moderate_drawdown: "Moderate portfolio loss",
    stress_test: "Difficult-market test",
  };
  return labels[key] ?? "Difficult-market scenario";
}

/**
 * Delegated to `@shared/publicRegime` so the SERVER prose composer reads the
 * same dictionary. Three authors wrote this one quantity; two of them put
 * different spellings on the same card. See that module's header.
 */
export { publicRegimeLabel };

export function publicAssetClassLabel(value: unknown): string {
  const key = keyOf(value);
  const labels: Record<string, string> = {
    crypto: "crypto markets",
    equity: "stock markets",
    equities: "stock markets",
    stock: "stock markets",
    forex: "currency markets",
    commodity: "commodity markets",
    commodities: "commodity markets",
    etf: "fund markets",
    vol_scaled: "markets grouped by the size of their recent moves",
  };
  return labels[key] ?? "the markets with enough verified history";
}

export function publicTimeWindowLabel(value: unknown): string {
  const raw = String(value ?? "").trim().toLowerCase();
  const hours = raw.match(/^(\d+)h$/);
  if (hours) return `${hours[1]}-hour window`;
  const days = raw.match(/^(\d+)d$/);
  if (days) return `${days[1]}-day window`;
  const weeks = raw.match(/^(\d+)w$/);
  if (weeks) return `${weeks[1]}-week window`;
  return "measured time window";
}

export const __PUBLIC_MARKET_LABELS_FOR_TESTS = PUBLIC_MARKET_LABELS;
